const LONG_ARRAY_LENGTH = 24;
const SAMPLE_COUNT = 6;

export interface AssistantArraySummary {
  length: number;
  min?: number;
  max?: number;
  average?: number;
  sample: unknown[];
}

const isPlainObject = (value: object): value is Record<string, unknown> => {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const sampleIndexes = (length: number): number[] => {
  if (length <= SAMPLE_COUNT) {
    return Array.from({ length }, (_value, index) => index);
  }

  const headCount = Math.ceil(SAMPLE_COUNT / 2);
  const tailCount = SAMPLE_COUNT - headCount;
  const indexes = Array.from({ length: headCount }, (_value, index) => index);
  for (let index = length - tailCount; index < length; index += 1) {
    indexes.push(index);
  }
  return indexes;
};

const summarizeValue = (value: unknown, seen: WeakSet<object>): unknown => {
  if (Array.isArray(value)) {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);
    const summarized = value.length > LONG_ARRAY_LENGTH
      ? summarizeLongArray(value, seen)
      : summarizeShortArray(value, seen);
    seen.delete(value);
    return summarized;
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  if (seen.has(value)) {
    return '[Circular]';
  }

  if (!isPlainObject(value)) {
    return value;
  }

  seen.add(value);
  let changed = false;
  const next: Record<string, unknown> = {};
  Object.keys(value).forEach(key => {
    const child = value[key];
    const summarizedChild = summarizeValue(child, seen);
    if (summarizedChild !== child) {
      changed = true;
    }
    next[key] = summarizedChild;
  });
  seen.delete(value);
  return changed ? next : value;
};

const summarizeShortArray = (values: unknown[], seen: WeakSet<object>): unknown[] => {
  let changed = false;
  const next = values.map(item => {
    const summarized = summarizeValue(item, seen);
    if (summarized !== item) {
      changed = true;
    }
    return summarized;
  });
  return changed ? next : values;
};

const summarizeLongArray = (values: unknown[], seen: WeakSet<object>): AssistantArraySummary => {
  let min: number | undefined;
  let max: number | undefined;
  let sum = 0;
  let numericCount = 0;

  values.forEach(item => {
    if (typeof item !== 'number' || !Number.isFinite(item)) {
      return;
    }
    numericCount += 1;
    sum += item;
    if (min === undefined || item < min) {
      min = item;
    }
    if (max === undefined || item > max) {
      max = item;
    }
  });

  const summary: AssistantArraySummary = {
    length: values.length,
    sample: sampleIndexes(values.length).map(index => summarizeValue(values[index], seen)),
  };
  if (min !== undefined && max !== undefined && numericCount > 0) {
    summary.min = min;
    summary.max = max;
    summary.average = sum / numericCount;
  }
  return summary;
};

/**
 * Copy of a model value for the assistant request.
 * Arrays longer than a short sample become length, min, max, average, and a few samples.
 * The input object is not modified.
 */
export const summarizeAssistantValue = (value: unknown): unknown => {
  return summarizeValue(value, new WeakSet());
};
